import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Plus, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useEvents } from '../../contexts/EventsContext';
import { useAuth } from '../../contexts/AuthContext';
import DatePicker from '../ui/DatePicker';
import TimePicker from '../ui/TimePicker';
import { useTranslation } from 'react-i18next';

interface StaffShiftChangesTabProps {
    userId: string;
    readOnly?: boolean;
}

const StaffShiftChangesTab: React.FC<StaffShiftChangesTabProps> = ({ userId, readOnly = false }) => {
    const { t } = useTranslation();
    const { shiftChanges, createShiftChange, deleteShiftChange } = useEvents();
    const { organization } = useAuth();

    const [isAdding, setIsAdding] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [shiftDate, setShiftDate] = useState<Date | undefined>();
    const [shiftOpen, setShiftOpen] = useState('');
    const [shiftClose, setShiftClose] = useState('');
    const [shiftBreakStart, setShiftBreakStart] = useState('');
    const [shiftBreakEnd, setShiftBreakEnd] = useState('');

    const userShiftChanges = (shiftChanges || []).filter((s: any) => s.hairdresser_id === userId);

    const handleAdd = async () => {
        if (!organization?.id) return toast.error(t('common.org_missing'));
        if (!shiftDate || !shiftOpen || !shiftClose) return toast.error(t('staff.timeoff.toast.data_missing'));

        setIsAdding(true);
        try {
            const payload: any = {
                shop_id: organization.id,
                hairdresser_id: userId,
                shift_date: format(shiftDate, 'yyyy-MM-dd'),
                start_time: shiftOpen,
                end_time: shiftClose,
            };

            if (shiftBreakStart && shiftBreakEnd) {
                payload.start_break = shiftBreakStart;
                payload.end_break = shiftBreakEnd;
            }

            const res = await createShiftChange(payload);
            if (res.success) {
                toast.success(t('staff.shiftchanges.toast.added'));
                setShiftDate(undefined);
                setShiftOpen('');
                setShiftClose('');
                setShiftBreakStart('');
                setShiftBreakEnd('');
            } else {
                toast.error(t('common.error'));
            }
        } catch (e) {
            toast.error(t('common.connection_error'));
        } finally {
            setIsAdding(false);
        }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            const res = await deleteShiftChange(id);
            if (res.success) {
                toast.success(t('staff.shiftchanges.toast.deleted'));
            } else {
                toast.error(t('common.error'));
            }
        } catch (e) {
            toast.error(t('common.error'));
        } finally {
            setDeletingId(null);
        }
    };

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
    };

    return (
        <motion.div
            key="shiftchanges"
            variants={tabContentVariants}
            initial="hidden" animate="visible" exit="exit"
            className="space-y-6 pt-4 pb-10 px-6"
        >
            {!readOnly && (
                <div className="bg-gray-50 rounded-2xl p-4 md:p-6 border border-gray-100 space-y-4">
                    {/* Row 1: Date */}
                    <div className="w-full space-y-1.5">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.day')}</label>
                        <DatePicker selected={shiftDate} onSelect={setShiftDate} placeholder={t('staff.timeoff.day')} minDate={new Date()} allowClosedDays={true} />
                    </div>

                    {/* Row 2: Open / Close */}
                    <div className="flex gap-4">
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.shiftchanges.open_time')}</label>
                            <TimePicker
                                value={shiftOpen}
                                onChange={setShiftOpen}
                                disabled={!shiftDate}
                                step={5}
                            />
                        </div>
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.shiftchanges.close_time')}</label>
                            <TimePicker
                                value={shiftClose}
                                onChange={setShiftClose}
                                disabled={!shiftDate}
                                disableBefore={shiftOpen}
                                step={5}
                            />
                        </div>
                    </div>

                    {/* Row 3: Break Start / Break End */}
                    <div className="flex gap-4">
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.shiftchanges.break_start')}</label>
                            <TimePicker
                                value={shiftBreakStart}
                                onChange={setShiftBreakStart}
                                disabled={!shiftDate || !shiftOpen}
                                disableBefore={shiftOpen}
                                step={5}
                            />
                        </div>
                        <div className="flex-1 space-y-1.5">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.shiftchanges.break_end')}</label>
                            <TimePicker
                                value={shiftBreakEnd}
                                onChange={setShiftBreakEnd}
                                disabled={!shiftDate || !shiftBreakStart}
                                disableBefore={shiftBreakStart}
                                step={5}
                            />
                        </div>
                    </div>

                    <button
                        onClick={handleAdd}
                        disabled={isAdding}
                        className="w-full h-[42px] px-6 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2"
                    >
                        {isAdding ? <Loader2 className="animate-spin w-4 h-4" /> : <Plus size={16} />}
                        {t('common.add')}
                    </button>
                </div>
            )}

            <div className="space-y-2">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <RefreshCw size={16} className="text-clip-500" />
                    {t('staff.shiftchanges.planned')}
                </h4>
                {userShiftChanges.map((s: any) => (
                    <div key={s.id} className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition-shadow">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                                <RefreshCw size={18} />
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                                <div className="text-xs font-bold text-gray-500 uppercase">{format(new Date(s.shift_date), 'dd MMM yyyy')}</div>
                                <span className="font-bold text-gray-900">{s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)}</span>
                                {s.start_break && s.end_break && (
                                    <span className="ml-2 text-xs text-gray-400 font-normal">
                                        ({t('staff.shiftchanges.break')}: {s.start_break?.slice(0, 5)}–{s.end_break?.slice(0, 5)})
                                    </span>
                                )}
                            </div>
                        </div>
                        {!readOnly && (
                            <button
                                onClick={() => handleDelete(s.id)}
                                disabled={deletingId === s.id}
                                className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                                {deletingId === s.id ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}
                            </button>
                        )}
                    </div>
                ))}
                {userShiftChanges.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-xl border border-dashed">{t('common.no_data')}</p>
                )}
            </div>
        </motion.div>
    );
};

export default StaffShiftChangesTab;
